import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useExam } from '../hooks/useExam';
import { Badge, Button, Card, LoadingState } from '../components/ui';
import ExamStatusCard from '../components/ExamStatusCard';
import AdBanner from '../components/AdBanner';
import './DashboardPage.css';
import { formatDate, formatExamCode, formatMinutes } from '../utils/format';
import { readExamMode } from '../utils/examPrefs';
import { examApi } from '../services/api';
import { getApiErrorCode, getShortExamInfo, shortExamMessage } from '../utils/examStart';

interface DashboardPageProps {
  onStartExam?: (sessionId: string) => void;
  onViewResults?: (sessionId: string) => void;
  onGoToExamSelection?: () => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onStartExam, onGoToExamSelection }) => {
  const { user } = useAuth();
  const { examTypes, currentSession, examHistory, isLoading, error } = useExam();
  const [notices, setNotices] = useState<any[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<any>(null);

  const showAds = !user?.paid_plan || user.paid_plan === 0;

  const metrics = useMemo(() => {
    const total = examHistory.length;
    const avg = total > 0 ? `${Math.round(examHistory.reduce((sum, exam) => sum + exam.percentage, 0) / total)}%` : '-';
    const best = total > 0 ? `${Math.max(...examHistory.map((exam) => exam.percentage))}%` : '-';
    const avgTime = total > 0
      ? formatMinutes(examHistory.reduce((sum, exam) => sum + (exam.timeTakenMinutes || 0), 0) / total)
      : '-';

    return [
      { id: 'available', label: '응시 가능 시험', value: String(examTypes.filter((type) => (type as { access?: string }).access !== 'locked').length) },
      { id: 'total', label: '완료한 시험', value: String(total) },
      { id: 'avg', label: '평균 점수', value: avg },
      { id: 'best', label: '최고 점수', value: best },
      { id: 'time', label: '평균 소요 시간', value: avgTime }
    ];
  }, [examHistory, examTypes.length]);

  const loadNotices = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/board/posts?category=notice&pinned_only=true&limit=5`);
      if (!response.ok) return;

      const result = await response.json();
      if (result.success) {
        setNotices(result.data.posts || []);
      }
    } catch {
      // Dashboard notices are optional context.
    }
  };

  useEffect(() => {
    loadNotices();
  }, []);

  const handleStartExam = () => {
    onGoToExamSelection?.();
  };

  // 가장 최근에 응시한 시험을 마지막 모드로 바로 다시 시작합니다 (시험 선택 단계 생략).
  const lastExamType = examHistory[0]?.examType;
  const lastExamAvailable = Boolean(lastExamType && examTypes.some((type) => type.exam_type_id === lastExamType && type.is_active && (type as { access?: string }).access !== 'locked'));
  const [quickStarting, setQuickStarting] = useState(false);
  const [quickStartError, setQuickStartError] = useState<string | null>(null);

  const handleQuickRestart = async (allowShort = false) => {
    if (!lastExamType || (quickStarting && !allowShort)) return;
    setQuickStarting(true);
    setQuickStartError(null);
    try {
      const response = await examApi.start(lastExamType, readExamMode(), { allowShort }) as { success: boolean; data?: { sessionId: string } };
      if (response.success && response.data?.sessionId) {
        onStartExam?.(response.data.sessionId);
      } else {
        setQuickStartError('시험을 시작하지 못했습니다. 시험 선택 화면에서 다시 시도해 주세요.');
      }
    } catch (error) {
      const shortInfo = getShortExamInfo(error);
      if (shortInfo && !allowShort) {
        if (window.confirm(`${shortExamMessage(shortInfo)}\n\n${shortInfo.available}문항으로 응시할까요?`)) {
          await handleQuickRestart(true);
        }
        return;
      }
      setQuickStartError(
        getApiErrorCode(error) === 'ACTIVE_SESSION_EXISTS'
          ? '진행 중인 시험이 있습니다. 먼저 이어서 풀거나 포기해 주세요.'
          : '시험을 시작하지 못했습니다. 시험 선택 화면에서 다시 시도해 주세요.',
      );
    } finally {
      setQuickStarting(false);
    }
  };

  const handleContinueExam = (sessionId: string) => {
    onStartExam?.(sessionId);
  };

  if (isLoading) {
    return <LoadingState label="대시보드 데이터를 불러오는 중입니다..." />;
  }

  return (
    <div className="dashboard-v2">
      {error && (
        <div className="dashboard-v2__error" role="alert">{error}</div>
      )}

      <section className="dashboard-v2__hero">
        <span className="dashboard-v2__hero-eyebrow">학습 대시보드</span>
        <h1>{user?.name ? `${user.name}님, ` : ''}다음 AWS 모의시험을 준비해볼까요?</h1>
        <p>응시 가능한 시험, 진행 중인 세션, 최근 결과를 한 화면에서 확인하세요.</p>
        <div className="dashboard-v2__hero-actions">
          {currentSession ? (
            <Button variant="secondary" size="lg" onClick={handleStartExam}>
              다른 시험 선택
            </Button>
          ) : lastExamAvailable ? (
            <>
              <Button variant="primary" size="lg" onClick={() => handleQuickRestart()} loading={quickStarting}>
                {formatExamCode(lastExamType)} 다시 응시 ({readExamMode() === 'practice' ? '연습' : '실전'} 모드)
              </Button>
              <Button variant="secondary" size="lg" onClick={handleStartExam}>
                다른 시험 선택
              </Button>
            </>
          ) : (
            <Button variant="primary" size="lg" onClick={handleStartExam} disabled={examTypes.length === 0}>
              모의시험 시작하기
            </Button>
          )}
        </div>
        {quickStartError && <p className="dashboard-v2__error" role="alert">{quickStartError}</p>}
      </section>

      <section className="dashboard-v2__status-wrap">
        <ExamStatusCard
          examTypes={examTypes}
          currentSession={currentSession}
          examHistory={examHistory}
          onStartExam={handleStartExam}
          onContinueExam={handleContinueExam}
        />
      </section>

      <section className="dashboard-v2__metrics-grid" aria-label="학습 지표">
        {metrics.map((metric) => (
          <Card key={metric.id} padding="md" className="dashboard-v2__metric">
            <span className="dashboard-v2__metric-label">{metric.label}</span>
            <strong className="dashboard-v2__metric-value">{metric.value}</strong>
          </Card>
        ))}
      </section>

      <Card padding="lg" tone="major" className="dashboard-v2__notices-card">
        <div className="dashboard-v2__section-head">
          <h3>공지사항</h3>
          <span>{notices.length}건</span>
        </div>

        {notices.length === 0 ? (
          <div className="dashboard-v2__empty">고정된 공지사항이 없습니다.</div>
        ) : (
          <ul className="dashboard-v2__notice-list">
            {notices.map((notice) => (
              <li key={notice.post_id}>
                <button
                  className="dashboard-v2__notice-btn"
                  onClick={() => setSelectedNotice(notice)}
                >
                  <span className="dashboard-v2__notice-title">
                    {notice.is_pinned && <Badge tone="info">고정</Badge>}
                    {notice.title}
                  </span>
                  <span className="dashboard-v2__notice-date tabular">{formatDate(notice.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AdBanner showAds={showAds} className="dashboard-v2__ad" />

      {selectedNotice && (
        <div className="dashboard-v2__modal" onClick={() => setSelectedNotice(null)}>
          <div className="dashboard-v2__modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-v2__modal-head">
              <h3>{selectedNotice.title}</h3>
              <button onClick={() => setSelectedNotice(null)} aria-label="공지 닫기">닫기</button>
            </div>
            <div className="dashboard-v2__modal-meta">
              <span>작성일 {formatDate(selectedNotice.created_at)}</span>
              <span>작성자 {selectedNotice.author_name}</span>
            </div>
            <div className="dashboard-v2__modal-body">
              {selectedNotice.content.split('\n').map((line: string, idx: number) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
